import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * TASK simulation. Used by Channel Selection / Task Completion / Tool Use /
 * Troubleshooting. The candidate picks an option per step, and the rubric
 * scores the sequence and choices.
 *
 * Events: step_completed:<id>, correct_action_taken, unsafe_action,
 * skipped_required_step, error_recovered, final_decision.
 */
export default function TaskSim({ scenario, recorder, onFinalize, busy }) {
  const steps = scenario.steps || [];
  const [picks, setPicks] = useState({});

  const select = (stepId, idx) => {
    const step = steps.find((s) => s.id === stepId);
    const opt = step.options[idx];
    setPicks({ ...picks, [stepId]: idx });
    recorder.log("step_completed", stepId);
    if (opt.correct) recorder.log("correct_action_taken", `${stepId}:${opt.label}`);
    if (opt.unsafe) recorder.log("unsafe_action", `${stepId}:${opt.label}`);
  };

  const finalize = () => {
    const allDone = steps.every((s) => picks[s.id] !== undefined);
    if (!allDone) recorder.log("skipped_required_step");
    const allCorrect = allDone && steps.every((s) => s.options[picks[s.id]]?.correct);
    onFinalize(allCorrect ? "all_correct" : "incomplete", { picks });
  };

  return (
    <div className="p-5 grid gap-4" data-testid="task-sim">
      {steps.map((s) => (
        <div key={s.id} className="paper border p-4" data-testid={`task-step-${s.id}`}>
          <div className="label-mono">Step · {s.id}</div>
          <h4 className="font-display text-base font-semibold mt-1">{s.label}</h4>
          <div className="mt-3 grid gap-2">
            {s.options.map((o, i) => (
              <button
                key={i}
                onClick={() => select(s.id, i)}
                data-testid={`task-${s.id}-opt-${i}`}
                className={`text-left p-3 border flex items-start gap-2 ${picks[s.id] === i ? "border-[var(--iddl-ink)] bg-[var(--iddl-surface)]" : "border-[var(--iddl-rule)] hover:bg-[var(--iddl-surface)]"}`}
              >
                {o.unsafe ? <AlertTriangle size={14} className="mt-0.5 text-cs" /> : <CheckCircle2 size={14} className="mt-0.5 opacity-40" />}
                <span className="text-sm">{o.label}</span>
                {o.correct && picks[s.id] === i && <span className="label-mono tint-ds border px-1.5 py-0.5 ml-auto">CORRECT</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="flex justify-end pt-4 border-t border-[var(--iddl-rule)]">
        <Button onClick={finalize} disabled={busy} className="btn-ink rounded-none" data-testid="btn-finalize-task">Finalise — submit task</Button>
      </div>
    </div>
  );
}
